import { P, match } from 'ts-pattern';

import type {
  LeaderboardEntry,
  LeaderboardEntryAcquiredVia,
  LeaderboardEntryShareholder,
} from '~/domains/contest/domains/services/interfaces/leaderboard.interface';

export type EntryOwnershipType =
  | 'my-original'
  | 'my-split'
  | 'my-marketplace-single-owner'
  | 'my-marketplace-multi-owner'
  | 'other-original'
  | 'other-multi-owner';

export type EntryShareholder = LeaderboardEntryShareholder & {
  isCurrentUser: boolean;
};

export type EntryOwnership = {
  type: EntryOwnershipType;
  isMultiOwner: boolean;
  isCurrentUserEntry: boolean;
  shareholders: EntryShareholder[];
  currentShareholder?: EntryShareholder;
  areShareholderAvatarsVisible: boolean;
  hasOwnershipDetail: boolean;
  isViewerPickController: boolean;
  isViewerEntryOriginator: boolean;
  isPartialEntry: boolean;
};

type EntryOwnershipInput = Pick<
  LeaderboardEntry,
  'isMultiOwner' | 'isViewerEntry' | 'shareholders'
>;

const MARKETPLACE_TYPES: EntryOwnershipType[] = [
  'my-marketplace-single-owner',
  'my-marketplace-multi-owner',
];

export const isMarketplaceAcquisition = (acquiredVia?: LeaderboardEntryAcquiredVia): boolean =>
  acquiredVia === 'marketplace_purchase' || acquiredVia === 'direct_offer';

const isSplitAcquisition = (acquiredVia?: LeaderboardEntryAcquiredVia): boolean =>
  acquiredVia === 'split';

const marketplaceType = (isMultiOwner: boolean): EntryOwnershipType =>
  isMultiOwner ? 'my-marketplace-multi-owner' : 'my-marketplace-single-owner';

type OwnershipTypeInput = {
  isMultiOwner: boolean;
  isCurrentUserEntry: boolean;
  currentAcquiredVia?: LeaderboardEntryAcquiredVia;
  hasMarketplaceShareholder: boolean;
  hasSplitShareholder: boolean;
};

const resolveOwnershipType = (input: OwnershipTypeInput): EntryOwnershipType =>
  match(input)
    .with(
      { currentAcquiredVia: P.union('marketplace_purchase', 'direct_offer') },
      ({ isMultiOwner }) => marketplaceType(isMultiOwner)
    )
    .with({ currentAcquiredVia: 'split' }, (): EntryOwnershipType => 'my-split')
    .with({ hasMarketplaceShareholder: true, isCurrentUserEntry: true }, ({ isMultiOwner }) =>
      marketplaceType(isMultiOwner)
    )
    .with(
      { hasSplitShareholder: true, isCurrentUserEntry: true },
      (): EntryOwnershipType => 'my-split'
    )
    .with({ isCurrentUserEntry: true }, (): EntryOwnershipType => 'my-original')
    .with({ isMultiOwner: true }, (): EntryOwnershipType => 'other-multi-owner')
    .otherwise((): EntryOwnershipType => 'other-original');

export const isMarketplaceOwnership = (type: EntryOwnershipType): boolean =>
  MARKETPLACE_TYPES.includes(type);

export type EntryOwnershipOptions = { isMarketplaceEnabled?: boolean };

const normaliseAcquiredVia = (
  acquiredVia: LeaderboardEntryAcquiredVia | undefined,
  isMarketplaceEnabled: boolean
): LeaderboardEntryAcquiredVia | undefined =>
  !isMarketplaceEnabled && isMarketplaceAcquisition(acquiredVia) ? 'split' : acquiredVia;

export const resolveEntryOwnership = (
  entry: EntryOwnershipInput,
  currentUserId?: string,
  { isMarketplaceEnabled = true }: EntryOwnershipOptions = {}
): EntryOwnership => {
  const isMultiOwner = !!entry.isMultiOwner;
  const isCurrentUserEntry = !!entry.isViewerEntry;

  const shareholders: EntryShareholder[] = (entry.shareholders ?? []).map((shareholder) => ({
    ...shareholder,
    acquiredVia: normaliseAcquiredVia(shareholder.acquiredVia, isMarketplaceEnabled),
    isCurrentUser: !!currentUserId && shareholder.user.id === currentUserId,
  }));

  const currentShareholder = shareholders.find((shareholder) => shareholder.isCurrentUser);

  const type = resolveOwnershipType({
    isMultiOwner,
    isCurrentUserEntry,
    currentAcquiredVia: currentShareholder?.acquiredVia,
    hasMarketplaceShareholder: shareholders.some((shareholder) =>
      isMarketplaceAcquisition(shareholder.acquiredVia)
    ),
    hasSplitShareholder: shareholders.some((shareholder) =>
      isSplitAcquisition(shareholder.acquiredVia)
    ),
  });

  return {
    type,
    isMultiOwner,
    isCurrentUserEntry,
    shareholders,
    currentShareholder,
    areShareholderAvatarsVisible:
      isMultiOwner && (!isMarketplaceOwnership(type) || !isCurrentUserEntry),
    hasOwnershipDetail: isMultiOwner || isMarketplaceOwnership(type),
    isViewerPickController: currentShareholder
      ? currentShareholder.isPickController
      : isCurrentUserEntry && !isMultiOwner,
    isViewerEntryOriginator: currentShareholder?.acquiredVia === 'original',
    isPartialEntry:
      isMarketplaceOwnership(type) ||
      shareholders.some((shareholder) => isMarketplaceAcquisition(shareholder.acquiredVia)),
  };
};

export const EMPTY_ENTRY_OWNERSHIP: EntryOwnership = resolveEntryOwnership({});
